/**
 * BWA-Parser (pure Function, frei von I/O).
 *
 * Eingabe: ein „rohes" Sheet als zweidimensionales Array (Zeilen × Spalten),
 * wie es z.B. exceljs liefert. Diese Funktion macht selbst keinen
 * XLSX-Parse — die UI-Schicht in apps/web extrahiert das Sheet und reicht
 * es hier herein. So bleibt der Core-Code testbar ohne Worker, FileReader
 * oder Browser-APIs.
 *
 * Heuristik:
 *   - Wir erkennen DATEV-BWA-Auswertungen anhand typischer Sektions-Marker:
 *     „Erlöse", „Umsatzerlöse", „Betriebliche Aufwendungen", „Roherträge",
 *     „Gesamtleistung", „Aufwand".
 *   - Eine Sektion läuft, bis die nächste Sektion oder eine Leerzeile
 *     auftaucht.
 *   - Innerhalb einer Sektion summieren wir alle numerischen Werte aus
 *     der „Saldo"-Spalte (die wir per Header-Zeilen-Heuristik finden).
 *   - Den Auswertungszeitraum (yearMonth) lesen wir aus einer Zeile mit
 *     „Auswertungszeitraum" / „Zeitraum" / einem ISO-/DE-Datums-Pattern.
 *
 * Die Heuristik ist BEWUSST konservativ: lieber eine ergebnislose Parse
 * (`{ status: 'unknown_format' }`) als falsche Werte. Berater entscheidet
 * im Anschluss, ob er die Erkennung übernimmt oder die Werte manuell
 * korrigiert.
 */

import type { MoneyCents } from '../schema/brands.js';
import type { MonthlyAggregate, YearMonth } from '../schema/financialDataset.js';

export interface ParsedRow {
  /** Roh-Zellwerte einer Zeile. Können string, number, oder null sein. */
  readonly values: ReadonlyArray<string | number | null | undefined>;
}

export interface ParsedSheet {
  readonly name: string;
  readonly rows: ReadonlyArray<ParsedRow>;
}

export type BwaParseResult =
  | {
      readonly status: 'ok';
      readonly month: MonthlyAggregate;
      readonly source: {
        readonly revenueRowsMatched: number;
        readonly costsRowsMatched: number;
        readonly yearMonthSource: 'header' | 'sheetname' | 'fallback';
      };
    }
  | {
      readonly status: 'unknown_format';
      readonly hint: string;
    }
  | {
      readonly status: 'incomplete';
      readonly partial: Partial<MonthlyAggregate>;
      readonly hint: string;
    };

const REVENUE_MARKERS = [
  'erlöse',
  'umsatzerlöse',
  'umsatzerloese',
  'gesamtleistung',
  'roherträge',
  'rohertraege',
  'einnahmen',
];

const COSTS_MARKERS = [
  'aufwand',
  'aufwendungen',
  'betriebliche aufwendungen',
  'betriebsausgaben',
  'gesamtkosten',
  'kosten',
];

const STOP_MARKERS = [
  'ergebnis',
  'jahresüberschuss',
  'jahresueberschuss',
  'eb-wert',
  'eb wert',
  'summe',
  'zwischenergebnis',
  'vorläufiges ergebnis',
];

const MONTHS_DE_TO_NUMBER: Readonly<Record<string, number>> = {
  januar: 1,
  februar: 2,
  märz: 3,
  maerz: 3,
  april: 4,
  mai: 5,
  juni: 6,
  juli: 7,
  august: 8,
  september: 9,
  oktober: 10,
  november: 11,
  dezember: 12,
};

function normalize(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function classifyRow(value: string): 'revenue' | 'costs' | 'stop' | null {
  const n = normalize(value);
  if (REVENUE_MARKERS.some((m) => n.includes(m))) return 'revenue';
  if (COSTS_MARKERS.some((m) => n.includes(m))) return 'costs';
  if (STOP_MARKERS.some((m) => n.includes(m))) return 'stop';
  return null;
}

/**
 * Eine Sektions-Header-Zeile hat *keine* Kontonummer in Spalte 0
 * (DATEV-Convention: Header → Bezeichnung in Spalte 0; Details →
 * Konto-Nr in Spalte 0). Damit unterscheiden wir „Erlöse" (Header)
 * von „Erlöse 19% USt" (Detail-Zeile mit Konto 8400).
 */
function isHeaderRow(row: ParsedRow): boolean {
  const first = row.values[0];
  if (typeof first === 'number') return false;
  if (typeof first !== 'string') return false;
  // Reine Zahl als String → auch Konto-Nr
  if (/^\d+$/.test(first.trim())) return false;
  return true;
}

function isNumericLike(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v !== 'string') return false;
  // Enthält keine Buchstaben außer optional EUR/€ am Ende; mindestens eine Ziffer.
  if (!/\d/.test(v)) return false;
  return /^[\s\d.,\-€]+(\s*EUR)?\s*$/i.test(v);
}

/**
 * Finde die wahrscheinliche „Saldo"/„Betrag"-Spalte. DATEV-Exporte haben
 * meist die Reihenfolge Konto | Bezeichnung | Saldo (Summe).
 *
 * Wir nehmen die Spalte mit den meisten numerischen Werten in den ersten
 * 50 Zeilen.
 */
function detectAmountColumn(rows: ReadonlyArray<ParsedRow>): number {
  const sampleSize = Math.min(rows.length, 50);
  // Wir bevorzugen rechte Spalten bei Gleichstand (DATEV-Konvention: Saldo
  // steht ganz rechts, Konto-Nr links).
  let bestCol = -1;
  let bestCount = 0;
  for (let c = 0; c < 12; c += 1) {
    let count = 0;
    for (let r = 0; r < sampleSize; r += 1) {
      const v = rows[r]?.values[c];
      if (isNumericLike(v)) count += 1;
    }
    if (count >= bestCount) {
      bestCount = count;
      bestCol = c;
    }
  }
  return bestCount > 0 ? bestCol : -1;
}

function asEuroAmount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    // DE-Format: 4.500,00 oder 4500,00 → number
    const cleaned = value
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');
    if (cleaned.length === 0) return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

function findYearMonth(
  rows: ReadonlyArray<ParsedRow>,
  sheetName: string,
): { value: YearMonth | null; source: 'header' | 'sheetname' | 'fallback' } {
  // Vollständiges Datum: DD.MM.YYYY oder DD/MM/YYYY oder DD-MM-YY
  const fullDateRe = /\b(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})\b/;
  // Kurzform: MM.YYYY oder MM/YYYY (z.B. „Zeitraum 04.2026")
  const shortDateRe = /\b(\d{1,2})[.\-/](\d{4})\b/;

  for (let r = 0; r < Math.min(rows.length, 30); r += 1) {
    for (const v of rows[r]?.values ?? []) {
      if (typeof v !== 'string') continue;
      const norm = v.toLowerCase();
      const isDateContext = norm.includes('zeitraum') || norm.includes('auswertung');
      if (isDateContext) {
        const fm = v.match(fullDateRe);
        if (fm && fm[2] && fm[3]) {
          const month = String(Number(fm[2])).padStart(2, '0');
          const year = fm[3].length === 2 ? `20${fm[3]}` : fm[3];
          return { value: `${year}-${month}`, source: 'header' };
        }
        const sm = v.match(shortDateRe);
        if (sm && sm[1] && sm[2]) {
          const month = String(Number(sm[1])).padStart(2, '0');
          return { value: `${sm[2]}-${month}`, source: 'header' };
        }
      }
      const monthName = Object.keys(MONTHS_DE_TO_NUMBER).find((mn) => norm.includes(mn));
      if (monthName) {
        const yearMatch = v.match(/(20\d{2})/);
        if (yearMatch) {
          const month = String(MONTHS_DE_TO_NUMBER[monthName]).padStart(2, '0');
          return { value: `${yearMatch[1]}-${month}`, source: 'header' };
        }
      }
    }
  }
  // Fallback: Sheet-Name kann „Januar 2026" oder „2026-01" enthalten
  const nameMatch = sheetName.match(/(20\d{2})[-_ ]?(\d{1,2})/);
  if (nameMatch && nameMatch[1] && nameMatch[2]) {
    const month = String(Number(nameMatch[2])).padStart(2, '0');
    return { value: `${nameMatch[1]}-${month}`, source: 'sheetname' };
  }
  return { value: null, source: 'fallback' };
}

export function parseBwaSheet(sheet: ParsedSheet): BwaParseResult {
  const amountCol = detectAmountColumn(sheet.rows);
  if (amountCol < 0) {
    return {
      status: 'unknown_format',
      hint: 'Keine numerische Spalte gefunden. Ist die Datei eine BWA?',
    };
  }

  let revenueCents = 0;
  let costsCents = 0;
  let revenueRowsMatched = 0;
  let costsRowsMatched = 0;
  let currentSection: 'revenue' | 'costs' | null = null;
  let foundAnySection = false;

  for (const row of sheet.rows) {
    // Sektions-Header-Erkennung: nur wenn Spalte 0 *kein* Konto-Number ist
    // und der String einen Marker enthält. Damit zählt „Erlöse 19% USt"
    // (Detail mit Konto 8400) NICHT als Sektion.
    if (isHeaderRow(row)) {
      const headerText = row.values[0] as string;
      const cls = classifyRow(headerText);
      if (cls === 'stop') {
        currentSection = null;
        continue;
      }
      if (cls === 'revenue' || cls === 'costs') {
        foundAnySection = true;
        const amount = asEuroAmount(row.values[amountCol]);
        if (amount !== null && Math.abs(amount) > 0) {
          // Header MIT Summe → diese Zahl übernehmen, Sektion direkt schließen
          if (cls === 'revenue') {
            revenueCents += Math.round(amount * 100);
            revenueRowsMatched += 1;
          } else {
            costsCents += Math.round(Math.abs(amount) * 100);
            costsRowsMatched += 1;
          }
          currentSection = null;
        } else {
          // Header ohne Summe → Sektion offen lassen, nachfolgende
          // Detail-Zeilen werden summiert
          currentSection = cls;
        }
        continue;
      }
    }

    // In einer aktiven Sektion: Detail-Zeilen-Beträge addieren
    if (currentSection !== null) {
      const amount = asEuroAmount(row.values[amountCol]);
      if (amount === null) continue;
      if (currentSection === 'revenue') {
        revenueCents += Math.round(amount * 100);
        revenueRowsMatched += 1;
      } else {
        costsCents += Math.round(Math.abs(amount) * 100);
        costsRowsMatched += 1;
      }
    }
  }

  if (!foundAnySection) {
    return {
      status: 'unknown_format',
      hint: 'Keine Erlös- oder Aufwands-Sektion erkannt. Ist die Auswertung als DATEV-BWA exportiert?',
    };
  }

  const ym = findYearMonth(sheet.rows, sheet.name);
  if (ym.value === null) {
    return {
      status: 'incomplete',
      partial: {
        revenueCents: revenueCents as MoneyCents,
        costsCents: costsCents as MoneyCents,
      },
      hint: 'Auswertungszeitraum konnte nicht ermittelt werden. Bitte Monat manuell auswählen.',
    };
  }

  return {
    status: 'ok',
    month: {
      yearMonth: ym.value,
      revenueCents: revenueCents as MoneyCents,
      costsCents: costsCents as MoneyCents,
    },
    source: {
      revenueRowsMatched,
      costsRowsMatched,
      yearMonthSource: ym.source,
    },
  };
}
